import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'

import { useAuthContext } from 'AuthProvider'
import { AgGridReact } from 'ag-grid-react'
import  { type GetMstTransportTypeTransportTypeGetRequest, type MstTransportType, type TransportType } from 'api-client'
import { EditStatusCellRenderer, LoadingOverlay } from 'components/commonparts/index'
import { useGridEdit } from 'hooks/useGridEdit'
import { useTransportType } from 'hooks/useTransportType'
import { type DataListPropsBase, initRecordEditEx, type RecordEditEx } from 'types/dataGridTypes'
import { validateRequired } from 'utils/validation'

import type { ColDef, ColGroupDef } from 'ag-grid-enterprise'

  const columnMapping: Record<string, string> = {
    transport_type_id: 'transportTypeId',
    transport_type_name: 'transportTypeName',
    max_weight: 'maxWeight',
    max_pallet_count: 'maxPalletCount',
    delete_flg: 'deleteFlg',
    remarks: 'remarks',
  }
  const primayKey = ['transportTypeId', 'transportTypeName']

  interface DataListProps extends DataListPropsBase {
    list: TransportType
    searchParam: GetMstTransportTypeTransportTypeGetRequest
    gridRef?: React.RefObject<AgGridReact>
    findSearchValue?: string
    onFindChanged?: (event: any) => void
    isSearchVisible?: boolean
  }

  interface MstTransportTypeWidthId extends MstTransportType, RecordEditEx {}

  function DataList({
    list,
    isLoading,
    searchParam,
    onReload,
    updateTrigger,
    onUploadData,
    setHasGridEdited,
    setResultMessage,
    setDownloadColumnDefs,
    gridRef: externalGridRef,
    findSearchValue: externalFindSearchValue,
    onFindChanged: externalOnFindChanged,
    isSearchVisible = false,
  }: DataListProps ) {
    const { loginUserInfo } = useAuthContext()
    const { fetchTransportTypeUpdate, isUpdating, result } = useTransportType()

    const internalGridRef = useRef<AgGridReact<MstTransportTypeWidthId>>(null)
    const gridRef = externalGridRef || internalGridRef
    
    const containerStyle = useMemo(() => {
      const searchBoxHeight = isSearchVisible ? 40 : 0
      return { width: '100%', height: `calc(100vh - ${120 + searchBoxHeight}px)` }
    }, [isSearchVisible])
    
    const gridStyle = useMemo(() => {
      const searchBoxHeight = isSearchVisible ? 40 : 0
      return { width: '100%', height: `calc(100vh - ${120 + searchBoxHeight}px)` }
    }, [isSearchVisible])
    const [row, setRow] = useState<MstTransportTypeWidthId[]>(
      list.list.map((item) => ({ ...item, ...initRecordEditEx() })))

    useEffect(() => {
      setResultMessage(result)
    }, [result])

    useEffect(() => {
      if (setDownloadColumnDefs) {
        setDownloadColumnDefs([
          {
            headerName: '輸送手段ID',
            children: [{ headerName: 'transport_type_id', field: 'transportTypeId', cellClass: 'numberType' }],
          },
          {
            headerName: '輸送手段名',
            children: [{ headerName: 'transport_type_name', field: 'transportTypeName', cellClass: 'stringType' }],
          },
          {
            headerName: '最大積載重量（ｔ）',
            children: [{ headerName: 'max_weight', field: 'maxWeight', cellClass: 'numberType' }],
          },
          {
            headerName: 'パレット最大積載枚数',
            children: [{ headerName: 'max_pallet_count', field: 'maxPalletCount', cellClass: 'numberType' }],
          },
          {
            headerName: '削除',
            children: [{ headerName: 'delete_flg', field: 'deleteFlg', cellClass: 'booleanType' }],
          },
          {
            headerName: '備考',
            children: [{ headerName: 'remarks', field: 'remarks', cellClass: 'stringType' }],
          },
        ])
      }
    }, [setDownloadColumnDefs])

    useEffect(() => {
      if (list.list.length === 0) {
        const blankRow = createBlankRow()
        setRow([blankRow])
      } else {
        setRow(list.list.map((item) => ({ ...item, ...initRecordEditEx() })))
        clearPinnedRows()
      }
    }, [list.list])

    const createBlankRow = useCallback((): MstTransportTypeWidthId => {
      const blankRow: Partial<MstTransportTypeWidthId> = {
        companyId: loginUserInfo?.companyId || null,
        transportTypeId: null,
        transportTypeName: null,
        maxWeight: null,
        maxPalletCount: null,
        deleteFlg: null,
        remarks: null,
        ...initRecordEditEx(true),
      }
      return blankRow as MstTransportTypeWidthId
    }, [])

    const fieldValidate = (pRow: MstTransportTypeWidthId, field: string | undefined, newValue: any): boolean => {
      let hasErrors = false
      switch (field) {
        case 'maxWeight': {
          hasErrors = checkAndSetError(pRow, field, validateRequired(newValue))
          break
        }
        case 'maxPalletCount': {
          hasErrors = checkAndSetError(pRow, field, validateRequired(newValue))
          break
        }
        case 'transportTypeName': {
          hasErrors = checkAndSetError(pRow, field, validateRequired(newValue))
          break
        }
        default:
          return false
      }
      return hasErrors
    }

    const updateMstTransportType = useCallback(
      async (updatedRows: MstTransportTypeWidthId[]) => {
        const datalist = updatedRows.map(
          ({ isEdited, transportTypeId, transportTypeName, editedFields, errors, ...rest }) => ({
            ...rest,
            transportTypeId: transportTypeId ?? null,
            transportTypeName: transportTypeName ?? '',
          })
        )

        try {
          await fetchTransportTypeUpdate({
            mstTransportTypeUpdate: { body: datalist },
          })
          if (onReload) onReload()
        } catch (err: any) {
          if (err?.errors && err.result?.status === 'error') {
            const gridApi = gridRef.current?.api

            err.errors.forEach((errrow: any) => {
              const { rowdata } = errrow
              const transportTypeId = (rowdata as { transport_type_id: string | null }).transport_type_id
              const errRow = updatedRows.find((rowdatas) => rowdatas.transportTypeId === transportTypeId)
              if (errRow) {
                errRow.errors = { orgSupplierId: [errrow.errorMessage] }
                const rowNode = gridApi?.getRowNode(errRow.id || '')
                if (rowNode) {
                  rowNode.setData(errRow)
                }
              }
            })
          }
        }
      },
      [fetchTransportTypeUpdate, searchParam]
    )

    const handleImportExcel = (data: any[]) => {
      let hasError = false
      const existingIds = new Set(list.list.map((item) => item.transportTypeId))
      const mappedData = data.map((excelRow: any) => {
        const mappedRow: Partial<MstTransportTypeWidthId> = {}
        Object.keys(columnMapping).forEach((key) => {
          if (key in excelRow) {
            const tableField = columnMapping[key as keyof typeof columnMapping]
            const importvalue = excelRow[key]
            let setvalue = importvalue || null

            if (['deleteFlg'].includes(tableField)) {
              setvalue = parseBoolean(importvalue)
            } else if(['transportTypeId'].includes(tableField)){
              setvalue = parsePositiveInteger(importvalue)
            } else if(['maxPalletCount'].includes(tableField)){
              setvalue = parsePositiveIntegerZero(importvalue)
            } else if(['maxWeight'].includes(tableField)){
              const num = parseNumeric(importvalue)
              setvalue = num !== null && num >= 0 ? Number(num.toFixed(2)) : null
            } else {
              setvalue = importvalue ? String(importvalue).trim() : null
            }
            if (!isNonInputValue(importvalue) && setvalue === null) hasError = true
            mappedRow[tableField as keyof MstTransportTypeWidthId] = setvalue as unknown as undefined
          }
        })
        if (
          mappedRow.transportTypeId &&
          !existingIds.has(mappedRow.transportTypeId)
        ) {
          delete mappedRow.transportTypeId;
        }
        return mappedRow as MstTransportTypeWidthId
      })
      
      return { mappedData, hasError }
    }

    const {
      checkAndSetError,
      longTextValueParser,
      longTextValueSetter,
      longTextValueFormatter,
      longTextEditorParams,
      isEnableEdit,
      parseBooleanValue,
      parseNumeric,
      numberValueParser,
      numberValueParserZero,
      numberValueFormatter,
      formatDecimalValue,
      parsePositiveInteger,
      parsePositiveIntegerZero,
      parseDecimalValue,
      formatBooleanValue,
      onGridReady,
      parseBoolean,
      isNonInputValue,
      localeText,
      autoSizeStrategy,
      editInfoValueGetter,
      clearPinnedRows,
    } = useGridEdit({
      gridRef,
      isLoading,
      isUpdating,
      onReload,
      onUploadData,
      updateTrigger,
      setHasGridEdited,
      setResultMessage,
      columnMapping,
      updateData: updateMstTransportType,
      handleImportExcel,
      createBlankRow,
      fieldValidate,
      primayKey,
      storageKey: 'gridColumnState:/master-transport-type',
      scrollPositionKey: 'transportTypeId',
      rowData: row,
    })

    const updateColumnDefs = (): (ColDef | ColGroupDef)[] => [
      {
        headerName: '!',
        rowDrag: true,
        cellRenderer: EditStatusCellRenderer,
        valueGetter: (params) => editInfoValueGetter(params),
        pinned: 'left',
        width: 100,
        filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              values: ['入力エラー', '変更', null],
              refreshValuesOnOpen: true,
              applyMiniFilterWhileTyping: true,
              excelMode: 'mac',
            },
          },
        ],
        buttons: ['reset'],
        closeOnApply: true,
        applyMiniFilterWhileTyping: true,
        excelMode: 'mac',
      },
      },
      {
        headerName: '輸送手段ID',
        field: 'transportTypeId',
        pinned: 'left',
        editable: false,
        width: 200,
      },
      {
        headerName: '輸送手段名',
        field: 'transportTypeName',
        pinned: 'left',
        editable: isEnableEdit(),
        // flex: 3
        width: 300
      },
      {
        headerName: '最大積載重量（ｔ）',
        field: 'maxWeight',
        editable: isEnableEdit(),
        cellClass: 'number-cell',
        filter: 'agNumberColumnFilter',
        cellEditor: 'agNumberCellEditor',
        flex: 1,
        // cellEditorParams: {
        //   step: 1e-18,
        //   min: 0,
        // },
        valueFormatter: (params) => formatDecimalValue(params, 2),
        valueParser: (params) => {
          const value = parseDecimalValue(params, 2);
          return value !== null && value < 0 ? null : value;
        },
      },
      {
        headerName: 'パレット最大積載枚数',
        field: 'maxPalletCount',
        editable: isEnableEdit(),
        cellClass: 'number-cell',
        filter: 'agNumberColumnFilter',
        cellEditor: 'agNumberCellEditor',
        flex: 1,
        // cellEditorParams: {
        //   step: 1e-18,
        //   min: 0,
        // },
        valueParser: numberValueParserZero,
        valueFormatter: numberValueFormatter,
      },
      {
        headerName: '削除',
        field: 'deleteFlg',
        editable: isEnableEdit(),
        flex: 1,
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        valueFormatter: formatBooleanValue,
        valueParser: parseBooleanValue,
      },
      {
        headerName: '備考',
        field: 'remarks',
        editable: isEnableEdit(),
        flex: 4,
        cellEditorPopup: true,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorParams: longTextEditorParams,
        valueFormatter: longTextValueFormatter,
        valueParser: longTextValueParser,
        valueSetter: longTextValueSetter,
      },
    ]

    const columnDefs = useMemo<ColDef[]>(updateColumnDefs, [])

    return (
      <div style={{ ...containerStyle, display: 'flex', flexDirection: 'column', height: '69vh' }}>
        <div style={{ flexGrow: 1, boxSizing: 'border-box' }}>
          <div style={gridStyle} className="ag-theme-balham">
            <AgGridReact
              ref={gridRef}
              getRowId={(params) => params.data.id}
              rowData={row}
              columnDefs={columnDefs}
              onGridReady={onGridReady}
              localeText={localeText}
              autoSizeStrategy={autoSizeStrategy}
              loadingOverlayComponent={LoadingOverlay}
              findSearchValue={externalFindSearchValue}
              onFindChanged={externalOnFindChanged}
            />
          </div>
        </div>
      </div>
    )
  }
export default DataList
export type { DataListProps }
